import React, { useState, useRef } from 'react';
import { Phone, Mail, AlertTriangle, GripVertical, ArrowRight, Calendar, Target, Building2, Users, MapPin, Banknote, Flame, GraduationCap, Heart, Landmark, Lightbulb, Clock, ShieldAlert, MessageSquare, Send, X, CheckCircle, XCircle, Printer } from 'lucide-react';
import { TagChip } from '../tags/TagChip';
import { langFlag } from '../../utils/tourLanguages';
import { SERVICE_TYPE_LABELS, SERVICE_TYPE_COLORS, commApi } from '../../services/crmApi';

const GRANT_PROGRAM_SHORT: Record<string, string> = {
  proo_1a: 'PROO 1a', proo_1b: 'PROO 1b', proo_2: 'PROO 2', nowefio: 'NOWEFIO',
  ferc: 'FERC', fe_malopolska: 'FE Małopolska', erasmus: 'Erasmus+', cerv: 'CERV',
  kreator_kultury: 'Kreator Kultury', nck: 'NCK', mhp: 'MHP', bgk_pes: 'BGK PES', inne: 'Inny',
};

interface PipelineStageInfo {
  stage_code: string;
  label: string;
  color: string;
  allowed_transitions: string[];
}

interface KanbanCardProps {
  app: any;
  statusField: string;
  onDragStart: (id: number) => void;
  onDragEnd: () => void;
  onClick: (id: number) => void;
  showLoanAmount?: boolean;
  stages?: PipelineStageInfo[];
  currentStageCode?: string;
  onStatusChange?: (appId: number, newStatus: string) => void;
  bulkMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: number) => void;
}

export const KanbanCard: React.FC<KanbanCardProps> = ({
  app,
  statusField,
  onDragStart,
  onDragEnd,
  onClick,
  showLoanAmount,
  stages,
  currentStageCode,
  onStatusChange,
  bulkMode,
  selected,
  onToggleSelect,
}) => {
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const daysOld = Math.floor((Date.now() - new Date(app.created_at).getTime()) / 86400000);
  const ageColor = daysOld < 7 ? 'text-green-600' : daysOld < 14 ? 'text-yellow-600' : 'text-red-600';
  const isGrant = app.service_type === 'granty_fpn';
  const amount = isGrant ? app.grant_fpn_requested_amount : (showLoanAmount ? app.loan_amount : app.requested_amount);
  const operatorName = app.assigned_operator_name;
  const operatorInitials = operatorName
    ? operatorName.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)
    : null;

  const currentStage = stages?.find(s => s.stage_code === currentStageCode);
  const transitions = currentStage?.allowed_transitions || [];

  return (
    <div
      draggable={!bulkMode}
      onDragStart={(e) => {
        if (bulkMode) { e.preventDefault(); return; }
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(app.id);
      }}
      onDragEnd={onDragEnd}
      onClick={() => {
        if (bulkMode && onToggleSelect) {
          onToggleSelect(app.id);
        } else {
          onClick(app.id);
        }
      }}
      className={`relative bg-white rounded-lg p-3 shadow-sm border cursor-pointer hover:shadow-md transition-all group ${
        selected ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50/30' : 'border-gray-200 hover:border-blue-300'
      }`}
    >
      {/* Header: name + move button + drag handle */}
      <div className="flex items-start justify-between gap-1">
        {bulkMode && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect?.(app.id)}
            onClick={(e) => e.stopPropagation()}
            className="mt-1 mr-1.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
            aria-label={`Zaznacz ${isGrant ? (app.grant_fpn_project_title || 'projekt') : `${app.first_name} ${app.last_name}`}`}
          />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h4 className="font-medium text-sm text-gray-900 truncate">
              {isGrant ? (app.grant_fpn_project_title || 'Bez tytułu') : `${app.first_name} ${app.last_name}`}
            </h4>
            {app.lead_score && (
              <span className={`text-[9px] font-bold px-1 py-0 rounded ${
                app.lead_score >= 75 ? 'bg-emerald-100 text-emerald-700' :
                app.lead_score >= 50 ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-500'
              }`}>{app.lead_score}</span>
            )}
          </div>
          <span className="text-[10px] text-gray-400">#{app.id}</span>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {/* WCAG 2.5.7: keyboard-accessible drag alternative */}
          {transitions.length > 0 && onStatusChange && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowMoveMenu(!showMoveMenu);
              }}
              className="p-1 text-gray-300 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
              aria-label={`Przenieś ${isGrant ? (app.grant_fpn_project_title || 'projekt') : `${app.first_name} ${app.last_name}`} do innego etapu`}
              aria-expanded={showMoveMenu}
              title="Przenieś do..."
            >
              <ArrowRight size={12} />
            </button>
          )}
          <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowNote(!showNote);
                setTimeout(() => noteRef.current?.focus(), 50);
              }}
              className="p-1 text-gray-300 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors opacity-0 group-hover:opacity-100"
              title="Szybka notatka"
            >
              <MessageSquare size={12} />
            </button>
          <GripVertical size={14} className="text-gray-300 group-hover:text-gray-500 mt-0.5" aria-hidden="true" />
        </div>
      </div>

      {/* Move dropdown (WCAG 2.5.7 drag alternative) */}
      {showMoveMenu && transitions.length > 0 && (
        <>
          <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setShowMoveMenu(false); }} />
          <div className="absolute right-1 top-8 z-20 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]">
            <div className="px-3 py-1 text-[10px] text-gray-400 uppercase tracking-wider font-medium">
              Przenieś do:
            </div>
            {transitions.map(targetCode => {
              const targetStage = stages?.find(s => s.stage_code === targetCode);
              if (!targetStage) return null;
              return (
                <button
                  key={targetCode}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange?.(app.id, targetCode);
                    setShowMoveMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                >
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${targetStage.color}`} />
                  {targetStage.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* SLA Badge */}
      {app.sla_status && app.sla_status !== 'ok' && (
        <div className={`mt-1.5 flex items-center gap-1 text-[10px] px-2 py-1 rounded-md font-medium ${
          app.sla_status === 'breach'
            ? 'bg-red-100 text-red-700 border border-red-200'
            : 'bg-amber-50 text-amber-700 border border-amber-200'
        }`}>
          {app.sla_status === 'breach' ? <ShieldAlert size={10} /> : <Clock size={10} />}
          <span>
            {app.sla_status === 'breach'
              ? `SLA przekroczone (${Math.abs(app.sla_days_left)}d po terminie)`
              : `SLA: ${app.sla_days_left}d pozostało`}
          </span>
        </div>
      )}

      {/* Tags */}
      {app.tags && app.tags.length > 0 && (
        <div className="flex flex-wrap gap-0.5 mt-1.5">
          {app.tags.slice(0, 3).map((tag: any) => (
            <TagChip key={tag.id} tag={tag} size="sm" />
          ))}
          {app.tags.length > 3 && (
            <span className="text-[10px] text-gray-400">+{app.tags.length - 3}</span>
          )}
        </div>
      )}

      {/* Service type badge */}
      {app.service_type && app.service_type !== statusField && (
        <span className={`inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${SERVICE_TYPE_COLORS[app.service_type] || 'bg-gray-100 text-gray-600'}`}>
          {SERVICE_TYPE_LABELS[app.service_type] || app.service_type}
        </span>
      )}

      {/* PUP: Conditions confirmation badge */}
      {app.service_type === 'pup_grant' && (
        <div className={`mt-1.5 flex items-center gap-1 text-[10px] px-2 py-1 rounded-md font-medium ${
          app.conditions_confirmed === true ? 'bg-green-100 text-green-700' :
          app.conditions_confirmed === false ? 'bg-red-100 text-red-700' :
          'bg-gray-100 text-gray-500'
        }`}>
          {app.conditions_confirmed === true ? <CheckCircle size={10} /> :
           app.conditions_confirmed === false ? <XCircle size={10} /> :
           <Clock size={10} />}
          {app.conditions_confirmed === true ? 'Warunki ✓' :
           app.conditions_confirmed === false ? 'Warunki ✗' : 'Warunki?'}
        </div>
      )}

      {/* Print ready indicator */}
      {(app.attachments_print_ready || app.application_print_ready) && (
        <div className="mt-1 flex items-center gap-1 text-[10px] text-green-600">
          <Printer size={10} />
          {app.attachments_print_ready && app.application_print_ready ? 'Druk: wszystko' :
           app.attachments_print_ready ? 'Druk: załączniki' : 'Druk: wniosek'}
        </div>
      )}

      {/* ── Product-specific enrichment ── */}

      {/* Granty FPN: program + deadline + priority */}
      {isGrant && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {app.grant_fpn_program_name && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium">
              {GRANT_PROGRAM_SHORT[app.grant_fpn_program_name] || app.grant_fpn_program_name}
            </span>
          )}
          {app.grant_fpn_priority && app.grant_fpn_priority !== 'medium' && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
              app.grant_fpn_priority === 'critical' ? 'bg-red-50 text-red-700' :
              app.grant_fpn_priority === 'high' ? 'bg-orange-50 text-orange-700' :
              'bg-slate-50 text-slate-600'
            }`}>
              <Target size={8} className="inline mr-0.5" />
              {app.grant_fpn_priority === 'critical' ? 'Krytyczny' : app.grant_fpn_priority === 'high' ? 'Wysoki' : 'Niski'}
            </span>
          )}
          {app.grant_fpn_deadline && (() => {
            const daysLeft = Math.ceil((new Date(app.grant_fpn_deadline).getTime() - Date.now()) / 86400000);
            const deadlineColor = daysLeft < 0 ? 'text-gray-400' : daysLeft <= 7 ? 'text-red-600' : daysLeft <= 30 ? 'text-amber-600' : 'text-green-600';
            return (
              <span className={`text-[10px] flex items-center gap-0.5 ${deadlineColor}`}>
                <Calendar size={8} />
                {daysLeft < 0 ? 'po terminie' : daysLeft === 0 ? 'dziś!' : `${daysLeft}d`}
              </span>
            );
          })()}
        </div>
      )}

      {/* Wycieczki: trasa, grupa, data, cena */}
      {app.service_type === 'wycieczki' && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {app.wycieczki_route && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 font-medium flex items-center gap-0.5">
              <MapPin size={8} />{({
                kombinat: 'Kombinat', socrealistyczna: 'Socrealizm', sakralna: 'Sakralna',
                prl: 'PRL', nocna: 'Nocna', objazdowa: 'Objazdowa', 'gra-cronos': 'Gra Cronos',
                'mieszkanie-z-epoki': 'Mieszkanie PRL', 'retro-przejazd-kombinat': 'Retro+Kombinat',
                'sesje-foto': 'Sesja foto', 'oferta-firmowa': 'Dla firm',
                architektura: 'Architektura', 'sladami-kina': 'Kino',
              } as Record<string, string>)[app.wycieczki_route] || app.wycieczki_route}
            </span>
          )}
          {app.wycieczki_group_size && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 flex items-center gap-0.5">
              <Users size={8} />{app.wycieczki_group_size} os.
            </span>
          )}
          {app.wycieczki_language && app.wycieczki_language !== 'pl' && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold uppercase border border-red-300">
              {langFlag(app.wycieczki_language)} {app.wycieczki_language.toUpperCase()}
            </span>
          )}
          {app.wycieczki_preferred_date && (() => {
            const d = Math.ceil((new Date(app.wycieczki_preferred_date).getTime() - Date.now()) / 86400000);
            const dateStr = new Date(app.wycieczki_preferred_date).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
            const isPast = d < 0;
            return (
              <span className={`text-[10px] flex items-center gap-0.5 font-medium ${isPast ? 'text-gray-400 line-through' : d <= 1 ? 'text-red-600 font-bold' : d <= 3 ? 'text-red-500' : d <= 7 ? 'text-amber-600' : 'text-teal-600'}`}>
                <Calendar size={8} />{dateStr}{!isPast && d <= 7 ? ` (${d <= 0 ? 'dziś!' : `za ${d}d`})` : ''}
              </span>
            );
          })()}
          {app.wycieczki_special_requests && (
            <span className="text-[10px] text-gray-400 truncate max-w-[120px]" title={app.wycieczki_special_requests}>
              <MessageSquare size={8} className="inline mr-0.5" />{app.wycieczki_special_requests.slice(0, 30)}{app.wycieczki_special_requests.length > 30 ? '...' : ''}
            </span>
          )}
        </div>
      )}

      {/* KFS: firma, szkolenie, pracownicy, budżet */}
      {app.service_type === 'kfs' && (
        <div className="mt-1.5 space-y-0.5">
          {(app.kfs_company_name || app.company_name) && (
            <p className="text-[10px] text-gray-600 truncate flex items-center gap-0.5">
              <Building2 size={8} className="text-amber-500 shrink-0" />{app.kfs_company_name || app.company_name}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1">
            {app.kfs_training_area && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium truncate max-w-[120px]">
                <GraduationCap size={8} className="inline mr-0.5" />{app.kfs_training_area}
              </span>
            )}
            {app.kfs_participant_count && (
              <span className="text-[10px] text-amber-600"><Users size={8} className="inline mr-0.5" />{app.kfs_participant_count} os.</span>
            )}
            {app.kfs_estimated_budget && (
              <span className="text-[10px] font-medium text-amber-700">{Number(app.kfs_estimated_budget).toLocaleString('pl-PL')} zł</span>
            )}
          </div>
        </div>
      )}

      {/* Dotacje na rozwój: firma, projekt, program, budżet */}
      {app.service_type === 'dotacje_rozwojowe' && (
        <div className="mt-1.5 space-y-0.5">
          {app.dev_grant_company_name && (
            <p className="text-[10px] text-gray-600 truncate flex items-center gap-0.5">
              <Building2 size={8} className="text-indigo-500 shrink-0" />{app.dev_grant_company_name}
            </p>
          )}
          {app.dev_grant_project_title && (
            <p className="text-[10px] text-indigo-700 font-medium truncate">{app.dev_grant_project_title}</p>
          )}
          <div className="flex flex-wrap items-center gap-1">
            {app.dev_grant_grant_program && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">{app.dev_grant_grant_program}</span>
            )}
            {app.dev_grant_total_cost && (
              <span className="text-[10px] font-medium text-indigo-700">{Number(app.dev_grant_total_cost).toLocaleString('pl-PL')} zł</span>
            )}
          </div>
        </div>
      )}

      {/* Czyste Powietrze: budynek, powierzchnia, ogrzewanie, dotacja */}
      {app.service_type === 'czyste_powietrze' && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {app.cp_building_type && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">{app.cp_building_type}</span>
          )}
          {app.cp_area_m2 && (
            <span className="text-[10px] text-green-600">{app.cp_area_m2} m²</span>
          )}
          {app.cp_current_heating && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600 flex items-center gap-0.5">
              <Flame size={8} />{app.cp_current_heating}
            </span>
          )}
          {app.cp_estimated_subsidy && (
            <span className="text-[10px] font-medium text-green-700">{Number(app.cp_estimated_subsidy).toLocaleString('pl-PL')} zł</span>
          )}
        </div>
      )}

      {/* Marketing Beauty: firma, branża, budżet, usługa */}
      {app.service_type === 'marketing_beauty' && (
        <div className="mt-1.5 space-y-0.5">
          {(app.mkt_company_name || app.company_name) && (
            <p className="text-[10px] text-gray-600 truncate flex items-center gap-0.5">
              <Building2 size={8} className="text-fuchsia-500 shrink-0" />{app.mkt_company_name || app.company_name}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1">
            {app.mkt_service_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 font-medium">{app.mkt_service_type}</span>
            )}
            {(app.mkt_industry || app.industry) && (
              <span className="text-[10px] text-fuchsia-600">{app.mkt_industry || app.industry}</span>
            )}
            {app.mkt_budget_monthly && (
              <span className="text-[10px] font-medium text-fuchsia-700">{Number(app.mkt_budget_monthly).toLocaleString('pl-PL')} zł/mies.</span>
            )}
          </div>
        </div>
      )}

      {/* Gastro Consulting: firma, typ, lokalizacja, przychód */}
      {app.service_type === 'gastro_consulting' && (
        <div className="mt-1.5 space-y-0.5">
          {(app.gastro_business_name || app.company_name) && (
            <p className="text-[10px] text-gray-600 truncate flex items-center gap-0.5">
              <Building2 size={8} className="text-orange-500 shrink-0" />{app.gastro_business_name || app.company_name}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1">
            {app.gastro_business_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-700 font-medium">{app.gastro_business_type}</span>
            )}
            {app.gastro_location && (
              <span className="text-[10px] text-orange-600 flex items-center gap-0.5"><MapPin size={8} />{app.gastro_location}</span>
            )}
            {app.gastro_seats_count && (
              <span className="text-[10px] text-orange-600">{app.gastro_seats_count} miejsc</span>
            )}
            {app.gastro_monthly_revenue && (
              <span className="text-[10px] font-medium text-orange-700">{Number(app.gastro_monthly_revenue).toLocaleString('pl-PL')} zł/mies.</span>
            )}
          </div>
        </div>
      )}

      {/* Hutnik Business: firma, status */}
      {app.service_type === 'hutnik_biznes' && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {app.company_name && (
            <span className="text-[10px] text-gray-600 truncate flex items-center gap-0.5">
              <Building2 size={8} className="text-lime-500" />{app.company_name}
            </span>
          )}
          {app.contract_type && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-lime-50 text-lime-700 font-medium">{app.contract_type}</span>
          )}
          {app.requested_amount && app.requested_amount !== 45000 && (
            <span className="text-[10px] font-medium text-lime-700">{Number(app.requested_amount).toLocaleString('pl-PL')} zł</span>
          )}
        </div>
      )}

      {/* ZUS: firma, dług, typ, rozwiązanie */}
      {app.service_type === 'zus' && (
        <div className="mt-1.5 space-y-0.5">
          {(app.zus_company_name || app.company_name) && (
            <p className="text-[10px] text-gray-600 truncate flex items-center gap-0.5">
              <Building2 size={8} className="text-violet-500 shrink-0" />{app.zus_company_name || app.company_name}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1">
            {app.zus_debt_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 font-medium">{app.zus_debt_type}</span>
            )}
            {app.zus_debt_amount && (
              <span className="text-[10px] font-medium text-red-600">{Number(app.zus_debt_amount).toLocaleString('pl-PL')} zł dług</span>
            )}
            {app.zus_requested_solution && (
              <span className="text-[10px] text-violet-600">{app.zus_requested_solution}</span>
            )}
          </div>
        </div>
      )}

      {/* BHP: firma, typ szkolenia, data, uczestnicy */}
      {app.service_type === 'bhp' && (
        <div className="mt-1.5 space-y-0.5">
          {(app.bhp_company_name || app.company_name) && (
            <p className="text-[10px] text-gray-600 truncate flex items-center gap-0.5">
              <Building2 size={8} className="text-red-500 shrink-0" />{app.bhp_company_name || app.company_name}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1">
            {app.bhp_training_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 font-medium">{app.bhp_training_type}</span>
            )}
            {app.bhp_participant_count && (
              <span className="text-[10px] text-red-600"><Users size={8} className="inline mr-0.5" />{app.bhp_participant_count} os.</span>
            )}
            {app.bhp_scheduled_date && (
              <span className="text-[10px] text-red-600 flex items-center gap-0.5">
                <Calendar size={8} />{new Date(app.bhp_scheduled_date).toLocaleDateString('pl-PL')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* STEAM: szkoła, typ, uczniowie */}
      {app.service_type === 'steam' && (
        <div className="mt-1.5 space-y-0.5">
          {app.steam_school_name && (
            <p className="text-[10px] text-gray-600 truncate flex items-center gap-0.5">
              <Landmark size={8} className="text-sky-500 shrink-0" />{app.steam_school_name}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1">
            {app.steam_school_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 font-medium">{app.steam_school_type}</span>
            )}
            {app.steam_student_count && (
              <span className="text-[10px] text-sky-600"><Users size={8} className="inline mr-0.5" />{app.steam_student_count} uczniów</span>
            )}
          </div>
        </div>
      )}

      {/* Usługi Księgowe: firma, NIP, pakiet */}
      {app.service_type === 'uslugi_ksiegowe' && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {app.company_name && (
            <span className="text-[10px] text-gray-600 truncate flex items-center gap-0.5">
              <Building2 size={8} className="text-slate-500" />{app.company_name}
            </span>
          )}
          {app.accounting_package && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-700 font-medium">{app.accounting_package}</span>
          )}
          {app.monthly_fee && (
            <span className="text-[10px] font-medium text-slate-700">{Number(app.monthly_fee).toLocaleString('pl-PL')} zł/mies.</span>
          )}
        </div>
      )}

      {/* Pożyczki: operator, zabezpieczenie, okres */}
      {(app.service_type === 'wsparcie_na_starcie' || app.service_type === 'pozyczki_rozwojowe') && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {app.loan_operator && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-pink-50 text-pink-700 font-medium">{app.loan_operator}</span>
          )}
          {app.security_form && (
            <span className="text-[10px] text-pink-600">{app.security_form}</span>
          )}
          {app.repayment_period && (
            <span className="text-[10px] text-pink-600">{app.repayment_period} mies.</span>
          )}
          {(app.loan_amount || app.requested_amount) && (
            <span className="text-[10px] font-medium text-pink-700">{Number(app.loan_amount || app.requested_amount).toLocaleString('pl-PL')} zł</span>
          )}
        </div>
      )}

      {/* Turnusy: brak dodatkowych kolumn w crm_applications poza trip_status — fallback na generyczne */}

      {/* Fallback: amount for products not covered above */}
      {!isGrant && !['wycieczki', 'kfs', 'dotacje_rozwojowe', 'czyste_powietrze', 'marketing_beauty', 'gastro_consulting', 'hutnik_biznes', 'zus', 'bhp', 'uslugi_ksiegowe', 'wsparcie_na_starcie', 'pozyczki_rozwojowe'].includes(app.service_type || '') && (
        <>
          {amount && (
            <div className="mt-1.5 text-xs font-medium text-gray-700">
              {Number(amount).toLocaleString('pl-PL')} zł
            </div>
          )}
          {app.business_idea && (
            <p className="mt-1 text-[11px] text-gray-500 line-clamp-1">{app.business_idea}</p>
          )}
        </>
      )}

      {/* Assigned operator */}
      {operatorInitials && (
        <div className="mt-1.5 flex items-center gap-1">
          <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[9px] font-bold flex items-center justify-center flex-shrink-0"
                title={operatorName}>
            {operatorInitials}
          </span>
          <span className="text-[10px] text-gray-400 truncate">{operatorName}</span>
        </div>
      )}

      {/* Document count badge */}
      {app.doc_count !== undefined && (
        <div className="mt-1.5 flex items-center gap-1 text-[10px]">
          <span className={`px-1.5 py-0.5 rounded-full font-medium ${
            app.doc_count === 0 ? 'bg-red-50 text-red-600' : app.doc_count < 3 ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'
          }`}>
            {app.doc_count} dok.
          </span>
        </div>
      )}

      {/* Smart next-action suggestion (rule-based) */}
      {(() => {
        const status = app[statusField] || app.status || '';
        const docs = app.doc_count ?? -1;
        const score = app.ai_score;
        const unread = app.unread_client_messages;
        let suggestion = '';
        let color = 'bg-blue-50 text-blue-700';

        if (unread && unread > 0) {
          suggestion = `${unread} wiadomości od klienta`;
          color = 'bg-rose-50 text-rose-700';
        } else if (docs === 0 && !['nowe_zgloszenie', 'zapytanie', 'nowy_lead'].includes(status)) {
          suggestion = 'Brak dokumentów — wyślij przypomnienie';
          color = 'bg-red-50 text-red-700';
        } else if (docs > 0 && docs < 3 && ['weryfikacja_dokumentów', 'kompletacja_dok'].includes(status)) {
          suggestion = 'Niekompletne dokumenty';
          color = 'bg-amber-50 text-amber-700';
        } else if (score && score >= 70 && ['analiza', 'analiza_kwalifikowalnosci'].includes(status)) {
          suggestion = `AI ${score}/100 — rekomenduj do złożenia`;
          color = 'bg-green-50 text-green-700';
        } else if (score && score < 40 && ['analiza', 'analiza_kwalifikowalnosci'].includes(status)) {
          suggestion = `AI ${score}/100 — omów ryzyka z klientem`;
          color = 'bg-red-50 text-red-700';
        } else if (daysOld > 14 && ['nowe_zgloszenie', 'zapytanie', 'nowy_lead'].includes(status)) {
          suggestion = 'Brak kontaktu >14d — follow-up';
          color = 'bg-amber-50 text-amber-700';
        } else if (daysOld > 7 && ['weryfikacja_dokumentów', 'kompletacja_dok'].includes(status)) {
          suggestion = 'Dokumenty >7d — przypomnij klientówi';
          color = 'bg-amber-50 text-amber-700';
        }

        return suggestion ? (
          <div className={`mt-1.5 flex items-center gap-1 text-[10px] px-2 py-1 rounded-md ${color}`}>
            <Lightbulb size={10} className="flex-shrink-0" />
            <span className="truncate">{suggestion}</span>
          </div>
        ) : null;
      })()}

      {/* Contact + age */}
      <div className="mt-2 flex items-center justify-between text-[10px] text-gray-400">
        {!isGrant ? (
          <div className="flex items-center gap-2">
            {app.phone && (
              <a href={`tel:${app.phone}`} onClick={e => e.stopPropagation()} className="flex items-center gap-0.5 hover:text-blue-600">
                <Phone size={10} />{app.phone.slice(-4)}
              </a>
            )}
            {app.email && (
              <a href={`mailto:${app.email}`} onClick={e => e.stopPropagation()} className="flex items-center gap-0.5 hover:text-blue-600">
                <Mail size={10} />
              </a>
            )}
          </div>
        ) : <div />}
        <span className={ageColor}>{daysOld}d</span>
      </div>

      {/* Warnings */}
      {app.warnings?.hasWarnings && (
        <div className="mt-1.5 flex items-center gap-1 text-[10px] text-amber-600">
          <AlertTriangle size={10} />
          <span>{app.warnings.issues?.length || 0} ostrz.</span>
        </div>
      )}

      {/* Quick note inline */}
      {showNote && (
        <div className="mt-2 border-t pt-2" onClick={(e) => e.stopPropagation()}>
          <textarea
            ref={noteRef}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && !e.shiftKey && noteText.trim()) {
                e.preventDefault();
                setSavingNote(true);
                try {
                  await commApi.addComment(app.id, { content: noteText.trim() });
                  setNoteText('');
                  setShowNote(false);
                } catch { /* ignore */ }
                setSavingNote(false);
              }
              if (e.key === 'Escape') { setShowNote(false); setNoteText(''); }
            }}
            placeholder="Notatka... (Enter = zapisz)"
            className="w-full text-xs border rounded px-2 py-1.5 resize-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
            rows={2}
            disabled={savingNote}
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-[9px] text-gray-400">Shift+Enter = nowa linia</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => { setShowNote(false); setNoteText(''); }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"><X size={12} /></button>
              <button type="button" disabled={!noteText.trim() || savingNote}
                onClick={async () => {
                  if (!noteText.trim()) return;
                  setSavingNote(true);
                  try {
                    await commApi.addComment(app.id, { content: noteText.trim() });
                    setNoteText('');
                    setShowNote(false);
                  } catch { /* ignore */ }
                  setSavingNote(false);
                }}
                className="p-1 text-emerald-500 hover:text-emerald-700 rounded disabled:opacity-30"><Send size={12} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
